import { useState, useEffect } from 'react'
import { X, Key, CheckCircle, AlertCircle } from 'lucide-react'
import { showToast } from './ui/Toast'

export default function AiSettings({ onClose }) {
  const [settings, setSettings] = useState({
    provider: 'openai',
    openaiKey: '',
    anthropicKey: '',
    kimiKey: '',
    geminiKey: '',
    xelsedModel: 'p5flash-alpha02', // xelsed model selection - best performer
    useServerKeys: true,
    // Claude-specific options (defaults)
    claudeExtendedThinking: true,
    claudeStructuredOutputs: false,
    // OpenAI-specific options
    openaiReasoningMode: 'high' // 'none', 'low', 'medium', 'high'
  })
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    // Load saved settings - gracefully handle Safari tracking prevention
    try {
      const savedSettings = localStorage.getItem('ai_settings')
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings))
      }
    } catch (error) {
      // Silent fail - Safari tracking prevention or storage blocked
    }
  }, [])

  const handleSave = () => {
    try {
      localStorage.setItem('ai_settings', JSON.stringify(settings))
      setSaved(true)
      showToast('AI settings saved', 'success')
      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (error) {
      // Safari tracking prevention blocked storage
      showToast('Settings could not be saved (browser blocking storage)', 'error')
    }
  }

  const handleChange = (field, value) => {
    // Auto-update model when provider changes
    if (field === 'provider') {
      setSettings(prev => {
        let newModel = prev.model
        if (value === 'anthropic') {
          newModel = 'claude-sonnet-4-5-20250929'
        } else if (value === 'openai') {
          newModel = 'gpt-5-mini' // GPT-5 mini with Responses API
        } else if (value === 'kimi') {
          newModel = 'kimi-k2-turbo-preview'
        } else if (value === 'gemini') {
          newModel = 'gemini-3-pro-preview'
        } else if (value === 'xelsed') {
          newModel = prev.xelsedModel || 'p5flash-alpha02'
        }
        return { ...prev, [field]: value, model: newModel }
      })
    } else {
      setSettings(prev => ({ ...prev, [field]: value }))
    }
    setSaved(false)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X size={24} />
        </button>

        <div className="p-6 pb-0">
          <h2 className="text-2xl font-bold mb-2">AI Assistant Settings</h2>
          <p className="text-gray-400 text-sm">
            Configure your AI provider and API keys
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-6">
          {/* Provider Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">AI Provider</label>
            <select
              value={settings.provider}
              onChange={(e) => handleChange('provider', e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-p5-pink"
            >
              <option value="openai">OpenAI (GPT-5.1 Mini - Responses API, faster & cheaper)</option>
              <option value="anthropic">Anthropic (Claude 4.5 Sonnet with Web Search)</option>
              <option value="kimi">Kimi AI (K2 Turbo - 256k context, ultra-fast)</option>
              <option value="gemini">Google Gemini (Gemini 3 Pro - Advanced Reasoning)</option>
              <option value="xelsed">Xelsed (Fine-tuned p5.js specialist)</option>
            </select>
          </div>

          {/* Use Server Keys Option */}
          <div className="bg-blue-500 bg-opacity-10 border border-blue-500 rounded p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.useServerKeys}
                onChange={(e) => handleChange('useServerKeys', e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Use Server API Keys</div>
                <div className="text-sm text-gray-400 mt-1">
                  Uses secure server-side keys. No setup required.
                </div>
                <div className="text-xs text-yellow-400 mt-2 flex items-start gap-1">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>
                    ⚠️ <strong>26-second timeout limit</strong> on server keys due to Cloudflare restrictions. 
                    For longer responses, uncheck this and use your own API key!
                  </span>
                </div>
              </div>
            </label>
          </div>

          {/* Client-side API Keys */}
          {!settings.useServerKeys && (
            <div className="space-y-4">
              <div className="bg-yellow-500 bg-opacity-10 border border-yellow-500 rounded p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-xs text-yellow-200">
                    Your API keys will be stored in your browser's local storage and used directly
                    from the client. They will never be sent to our servers.
                  </p>
                </div>
              </div>

              {/* Only show API key field for selected provider */}
              {settings.provider === 'openai' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    OpenAI API Key
                    <span className="text-gray-500 text-xs ml-2">(for GPT-5.1 mini)</span>
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="password"
                      value={settings.openaiKey}
                      onChange={(e) => handleChange('openaiKey', e.target.value)}
                      placeholder="sk-..."
                      className="w-full bg-gray-900 border border-gray-700 rounded pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-p5-pink font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your key from{' '}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-p5-blue hover:underline"
                    >
                      platform.openai.com/api-keys
                    </a>
                  </p>
                </div>
              )}

              {settings.provider === 'anthropic' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Anthropic API Key
                    <span className="text-gray-500 text-xs ml-2">(for Claude)</span>
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="password"
                      value={settings.anthropicKey}
                      onChange={(e) => handleChange('anthropicKey', e.target.value)}
                      placeholder="sk-ant-..."
                      className="w-full bg-gray-900 border border-gray-700 rounded pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-p5-pink font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your key from{' '}
                    <a
                      href="https://console.anthropic.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-p5-blue hover:underline"
                    >
                      console.anthropic.com
                    </a>
                  </p>
                </div>
              )}

              {settings.provider === 'kimi' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Kimi AI API Key
                    <span className="text-gray-500 text-xs ml-2">(for K2 Turbo)</span>
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="password"
                      value={settings.kimiKey}
                      onChange={(e) => handleChange('kimiKey', e.target.value)}
                      placeholder="sk-..."
                      className="w-full bg-gray-900 border border-gray-700 rounded pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-p5-pink font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your key from{' '}
                    <a
                      href="https://platform.moonshot.ai/console/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-p5-blue hover:underline"
                    >
                      platform.moonshot.ai
                    </a>
                  </p>
                </div>
              )}

              {settings.provider === 'gemini' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Google Gemini API Key
                    <span className="text-gray-500 text-xs ml-2">(for Gemini 3 Pro via Vertex AI)</span>
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="password"
                      value={settings.geminiKey}
                      onChange={(e) => handleChange('geminiKey', e.target.value)}
                      placeholder="AIza..."
                      className="w-full bg-gray-900 border border-gray-700 rounded pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-p5-pink font-mono text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your key from{' '}
                    <a
                      href="https://aistudio.google.com/app/apikey"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-p5-blue hover:underline"
                    >
                      aistudio.google.com/app/apikey
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* OpenAI advanced options */}
          {settings.provider === 'openai' && (
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-medium mb-3 text-sm">GPT-5.1 Reasoning Mode</h3>
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-300 mb-2">
                  Choose reasoning effort (GPT-5.1 Cookbook):
                </label>
                <select
                  value={settings.openaiReasoningMode || 'low'}
                  onChange={(e) => handleChange('openaiReasoningMode', e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-p5-pink"
                >
                  <option value="none">None - No reasoning tokens (fastest, like GPT-4.1)</option>
                  <option value="low">Low - Minimal reasoning (fast, economical)</option>
                  <option value="medium">Medium - Balanced reasoning & speed</option>
                  <option value="high">High - Maximum reasoning (complex problems)</option>
                </select>
                <div className="text-xs text-gray-400 mt-2 space-y-1">
                  <p>• <strong>none</strong>: Forces no reasoning tokens, fastest responses</p>
                  <p>• <strong>low</strong>: Best for chat and simple coding tasks (recommended)</p>
                  <p>• <strong>medium</strong>: Better for complex logic and debugging</p>
                  <p>• <strong>high</strong>: Most thorough, uses more tokens</p>
                </div>
              </div>
            </div>
          )}

          {/* Claude advanced options */}
          {settings.provider === 'anthropic' && (
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-medium mb-2 text-sm">Claude 4.5 options</h3>
              <div className="space-y-3 text-xs text-gray-300">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.claudeExtendedThinking !== false}
                    onChange={(e) => handleChange('claudeExtendedThinking', e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Enable extended thinking</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Improves reasoning for complex coding tasks by letting Claude think more deeply before responding.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.claudeStructuredOutputs}
                    onChange={(e) => handleChange('claudeStructuredOutputs', e.target.checked)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Enable structured outputs (JSON)</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Returns Claude responses in a structured JSON format on the backend, useful for advanced workflows.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Xelsed model options */}
          {settings.provider === 'xelsed' && (
            <div className="bg-gray-900 rounded p-4">
              <h3 className="font-medium mb-3 text-sm">Xelsed Model Selection</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-2">
                    Choose fine-tuned model:
                  </label>
                  <select
                    value={settings.xelsedModel || 'p5flash-alpha02'}
                    onChange={(e) => handleChange('xelsedModel', e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-p5-pink"
                  >
                    <option value="p5flash-alpha02">p5flash-alpha02 (p5.js specialist - BEST)</option>
                    <option value="xelsed-alpha03-flash">xelsed-alpha03-flash (Alpha03 Flash)</option>
                    <option value="xelsed-alpha06-flash">xelsed-alpha06-flash (Alpha06 Flash)</option>
                    <option value="xelsed-alpha6-lite">xelsed-alpha6-lite (lightweight, faster)</option>
                    <option value="alpha01-test">alpha01-test (legacy test model)</option>
                  </select>
                  <div className="text-xs text-gray-400 mt-2 space-y-1">
                    <p>• <strong>p5flash-alpha02</strong>: Fine-tuned Gemini 2.5 Flash for p5.js</p>
                    <p>• <strong>xelsed-alpha03-flash</strong>: Alpha03 Flash model</p>
                    <p>• <strong>xelsed-alpha06-flash</strong>: Alpha06 Flash model</p>
                    <p>• <strong>xelsed-alpha6-lite</strong>: Lightweight model, faster responses</p>
                    <p className="text-green-300">✨ Best performing model for p5.js creative coding</p>
                    <p className="text-yellow-300">⚠️ Server keys only - uses Vertex AI endpoint</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* How to Get Keys */}
          <div className="bg-gray-900 rounded p-4">
            <h3 className="font-medium mb-2 text-sm">How to get API keys:</h3>
            <div className="space-y-2 text-xs text-gray-400">
              <div>
                <p className="font-medium text-white">OpenAI (GPT-5.1 Mini with Responses API):</p>
                <p className="text-green-300 text-xs mb-1">⚡ 3% better performance, 40-80% lower costs via caching</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Visit platform.openai.com</li>
                  <li>Sign up or login</li>
                  <li>Go to API Keys section</li>
                  <li>Create new secret key</li>
                  <li>Copy and paste above</li>
                </ol>
                <p className="text-gray-500 mt-1">Note: Uses new Responses API with reasoning and agentic primitives</p>
              </div>
              <div className="pt-2">
                <p className="font-medium text-white">Anthropic (Claude 4.5 Sonnet):</p>
                <p className="text-yellow-300 text-xs mb-1">✨ Includes real-time web search with citations</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Visit console.anthropic.com</li>
                  <li>Sign up or login</li>
                  <li>Go to API Keys</li>
                  <li>Create new key</li>
                  <li>Copy and paste above</li>
                </ol>
                <p className="text-gray-500 mt-1">Note: Web searches cost $10 per 1,000 searches</p>
              </div>
              <div className="pt-2">
                <p className="font-medium text-white">Kimi AI (K2 Turbo):</p>
                <p className="text-green-300 text-xs mb-1">⚡ Ultra-fast 60-100 tokens/s, 256k context</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Visit platform.moonshot.ai</li>
                  <li>Sign up or login</li>
                  <li>Go to Console → API Keys</li>
                  <li>Create new key</li>
                  <li>Copy and paste above</li>
                </ol>
                <p className="text-gray-500 mt-1">Note: Excellent for coding with MoE architecture</p>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button - Fixed at bottom */}
        <div className="p-6 pt-3 border-t border-gray-700">
          <button
            onClick={handleSave}
            className={`w-full py-3 rounded font-medium flex items-center justify-center gap-2 ${
              saved
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-p5-pink hover:bg-pink-600'
            }`}
          >
            {saved ? (
              <>
                <CheckCircle size={20} />
                <span>Settings Saved!</span>
              </>
            ) : (
              <>
                <Key size={20} />
                <span>Save Settings</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
